import React from "react";
import { useState, useEffect } from "react";

// Libraries
import { Col, Container, Navbar, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { m } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../hooks/useLanguage";
import { Helmet } from "react-helmet-async";

// Components
import Header, { HeaderNav, Menu } from "../Components/Header/Header";
import { fadeIn } from "../Functions/GlobalAnimations";
import Footer from "../Components/Footer";
import ModalPopupContact from "../Components/ModalPopupContact";
import Preloader from "../Components/Preloader";
import Buttons from "../Components/Button/Buttons";

const Tos = (props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { t } = useTranslation(['tos', 'common', 'homepage']);
  const { getLocalizedPath } = useLanguage();

  const preloaderText = {
    marketingova: t('homepage:preloader.marketingova'),
    b2b: t('homepage:preloader.b2b'),
    agentura: t('homepage:preloader.agentura')
  };

  useEffect(() => {
    // Simulate loading time and hide preloader
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Adjust time as needed

    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('tos:meta.title')}</title>
        <meta name='description' content={t('tos:meta.description')} />
      </Helmet>
      <Preloader isLoading={isLoading} text={preloaderText} />
      <div className='inaqpage' style={props.style}>
        {/* Header Start */}
        <Header
          topSpace={{ md: true }}
          type='reverse-scroll'
          width='100%'
          className='w-full bg-inaqpurple'
        >
          {" "}
          <HeaderNav
            theme='light'
            expand='lg'
            bg='inaqpurple' // Add background color to header
            containerClass='sm:!px-0'
            className='py-[0px] px-[35px] md:pr-[15px] md:pl-[10px] md:py-[12px] max-w-[1280px] mx-auto'
            fluid='fluid'
          >
            <Col sm={6} lg={2} xs='auto' className='me-auto ps-0'>
              <Link
                aria-label='logo home'
                className='flex items-center'
                to={getLocalizedPath('/')}
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <Navbar.Brand className='inline-block p-0 m-0 '>
                  <img
                    className='default-logo'
                    width='56'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />

                  <img
                    className='mobile-logo'
                    width='111'
                    height='36'
                    loading='lazy'
                    src='/assets/img/webp/inaq_logo_web.webp'
                    data-rjs='/assets/img/webp/inaq_logo_web.webp'
                    alt='logo'
                  />
                </Navbar.Brand>
              </Link>
            </Col>
            <Navbar.Toggle className='order-last md:ml-[17px] md:px-[15px]'>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
              <span className='navbar-toggler-line'></span>
            </Navbar.Toggle>
            <Navbar.Collapse className='col-auto justify-center menu-order inaq-header-menu px-lg-0 col-lg-8'>
              <Menu {...props} />
            </Navbar.Collapse>
            <Col xs='auto' lg={2} className='text-right pe-0'>
              <Buttons
                type='button'
                onClick={() => setIsModalOpen(true)}
                className='rounded-[2px] font-medium font-rubik uppercase shadow-[0_0_30px_rgba(0,0,0,0.08)] md:hidden hover:text-inaqgold'
                themeColor={["#fff", "#fff"]}
                size='xs'
                backgroundColor='transparent'
                color='#212529'
                title={t('tos:header.button')}
              />
            </Col>
          </HeaderNav>
        </Header>
        {/* Header End */}
        <ModalPopupContact
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />

        <section className='mt-24 sm:mt-32 py-[20px] max-w-[940px] lg:max-w-[768px] md:max-w-[690px] sm:max-w-[510px] xs:max-w-full xs:px-[15px] mx-auto'>
          <Container>
            <Row>
              <Col className='px-0 text-sm font-rubik m-0'>
                <div className='flex text-gray-400 gap-1'>
                  <Link
                    aria-label='link for home'
                    to={getLocalizedPath('/')}
                    className='text-gray-400 hover:text-inaqgold px-0'
                  >
                    {t('tos:breadcrumb.home')}
                  </Link>
                  <span className='mx-2'>&gt;</span>
                  <span>{t('tos:breadcrumb.tos')}</span>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Section Start */}
        <m.section className='py-12 sm:py-[50px] xs:py-[40px]' {...fadeIn}>
          <Container>
            <Row className='justify-center'>
              <Col lg={10} md={12} className='col-12'>
                <h2 className="font-['Oxanium'] text-inaqpurple font-medium text-[32px] mb-[30px] text-center">
                  {t('tos:title')}
                </h2>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:intro.paragraph1')}
                </p>

                <h3 className="font-['Oxanium'] text-inaqpurple font-medium text-[26px] mb-[20px] mt-[40px]">
                  {t('tos:section1.title')}
                </h3>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section1.paragraph1')}
                </p>

                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section1.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section1.paragraph3')}
                </p>

                <h3 className="font-['Oxanium'] text-inaqpurple font-medium text-[26px] mb-[20px] mt-[40px]">
                  {t('tos:section2.title')}
                </h3>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section2.paragraph1')}
                </p>

                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section2.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section2.paragraph3')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section2.paragraph4')}
                </p>

                <h3 className="font-['Oxanium'] text-inaqpurple font-medium text-[26px] mb-[20px] mt-[40px]">
                  {t('tos:section3.title')}
                </h3>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.paragraph1')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section3.subsection1.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection1.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection1.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection1.paragraph3')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section3.subsection2.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection2.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection2.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection2.paragraph3')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section3.subsection3.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection3.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection3.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection3.paragraph3')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section3.subsection4.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection4.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection4.paragraph2')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection4.paragraph3')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section3.subsection4.paragraph4')}
                </p>

                <h3 className="font-['Oxanium'] text-inaqpurple font-medium text-[26px] mb-[20px] mt-[40px]">
                  {t('tos:section4.title')}
                </h3>
                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section4.subsection1.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection1.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection1.paragraph2')}
                </p>
                <ul className='list-disc pl-[30px] mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  <li>{t('tos:section4.subsection1.list.0')}</li>
                  <li>{t('tos:section4.subsection1.list.1')}</li>
                  <li>{t('tos:section4.subsection1.list.2')}</li>
                </ul>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection1.paragraph3')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection1.paragraph4')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section4.subsection2.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection2.paragraph1')}
                </p>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section4.subsection2.paragraph2')}
                </p>

                <h3 className="font-['Oxanium'] text-inaqpurple font-medium text-[26px] mb-[20px] mt-[40px]">
                  {t('tos:section5.title')}
                </h3>
                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section5.subsection1.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section5.subsection1.paragraph1')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section5.subsection2.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section5.subsection2.paragraph1')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section5.subsection3.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section5.subsection3.paragraph1')}
                </p>

                <h4 className="font-['Oxanium'] text-inaqpurple font-medium text-[20px] mb-[15px] mt-[30px]">
                  {t('tos:section5.subsection4.title')}
                </h4>
                <p className='mb-[25px] md:mb-[20px] sm:mb-[15px] font-rubik text-[#828282]'>
                  {t('tos:section5.subsection4.paragraph1')}
                </p>
              </Col>
            </Row>
          </Container>
        </m.section>
        {/* Section End */}

        {/* Footer Start */}
        <Footer
          className='inaq-footer'
          theme='light'
          logo='/assets/img/webp/inaq_logo.svg'
          width='40'
          objectFit='contain'
          loading='lazy'
        />
        {/* Footer End */}
      </div>
    </>
  );
};

export default Tos;
